import { FC, ReactNode } from 'react'
import Box from '@mui/system/Box'
import { Grid, Typography } from '@mui/material'
import format from 'date-fns/format'
import JsonView from '@uiw/react-json-view'
import { BleConnectionLog } from 'src/api'
import { InnerLink } from 'src/components/ui'

interface BleConnectionLogInfoProps {
  log: BleConnectionLog
}

interface InfoItem {
  name: string
  value: ReactNode
}

export const BleConnectionLogInfo: FC<BleConnectionLogInfoProps> = ({ log }) => {
  const {
    id,
    category,
    deviceType,
    step,
    eventType,
    status,
    platform,
    appVersion,
    deviceOS,
    dataGrabberId,
    occurredAt,
    createdAt,
    updatedAt,
    data,
    contractor,
    contractorId,
    project,
    projectId,
    projectSection,
    projectSectionId,
    user,
    userId,
    probeRawId,
    event,
    eventId,
  } = log

  const generalInformation: InfoItem[] = [
    { name: 'ID', value: id ?? '-' },
    { name: 'Category', value: category || '-' },
    { name: 'Device type', value: deviceType || '-' },
    { name: 'Step', value: step || '-' },
    { name: 'Event type', value: eventType || '-' },
    { name: 'Status', value: status || '-' },
    { name: 'Occurred at', value: occurredAt ? format(new Date(occurredAt), 'PPpp') : '-' },
    { name: 'Created at', value: createdAt ? format(new Date(createdAt), 'PPpp') : '-' },
    { name: 'Updated at', value: updatedAt ? format(new Date(updatedAt), 'PPpp') : '-' },
  ]

  const metaInformation: InfoItem[] = [
    {
      name: 'User',
      value: user?.name && userId ? <InnerLink to={`/users/${userId}`}>{user.name}</InnerLink> : '-',
    },
    {
      name: 'Contractor',
      value: contractor?.name && contractorId ? <InnerLink to={`/contractors/${contractorId}`}>{contractor.name}</InnerLink> : '-',
    },
    {
      name: 'Project',
      value: project?.name && projectId ? <InnerLink to={`/projects/${projectId}`}>{project.name}</InnerLink> : '-',
    },
    {
      name: 'Section',
      value: projectSection?.name && projectSectionId ? <InnerLink to={`/sections/${projectSectionId}`}>{projectSection.name}</InnerLink> : '-',
    },
    {
      name: 'RapidRH probe',
      value: probeRawId ? <InnerLink to={`/wagner-probes/${probeRawId}`}>{probeRawId}</InnerLink> : '-',
    },
    {
      name: 'Event name',
      value: eventId
        ? <InnerLink to={`/events/${eventId}`}>{event?.eventCardRenderingData?.eventName || event?.eventCardRenderingData?.eventDetails || eventId}</InnerLink>
        : '-',
    },
    { name: 'Platform', value: platform || '-' },
    { name: 'App version', value: appVersion || '-' },
    { name: 'Device OS', value: deviceOS || '-' },
    { name: 'Data grabber ID', value: dataGrabberId || '-' },
  ]

  const renderItems = (items: InfoItem[]) => items.map((item, index) => (
    <Grid key={index} item xs={12} sx={{ py: 0.5 }}>
      <Typography variant="body1" component="div">
        <Typography variant="body1" fontWeight={500} display="inline">{item.name}:</Typography> {item.value}
      </Typography>
    </Grid>
  ))

  return (
    <Box width="100%" pt={2}>
      <Grid container spacing={4}>
        <Grid container xs={12} md={6} item spacing={2}>
          <Box width="100%" pl="16px">
            <Grid item xs={12} sx={{ py: 1 }}>
              <Typography variant="h6" fontWeight={500}>General information</Typography>
            </Grid>
            {renderItems(generalInformation)}
          </Box>
        </Grid>
        <Grid container xs={12} md={6} item spacing={2}>
          <Box width="100%" pl="16px">
            <Grid item xs={12} sx={{ py: 1 }}>
              <Typography variant="h6" fontWeight={500}>Meta information</Typography>
            </Grid>
            {renderItems(metaInformation)}
          </Box>
        </Grid>
        <Grid container xs={12} item spacing={2}>
          <Box width="100%" pl="16px">
            <Grid item xs={12} sx={{ py: 1 }}>
              <Typography variant="h6" fontWeight={500}>Data</Typography>
            </Grid>
            <Grid item xs={12} sx={{ py: 0.5 }}>
              {data && Object.keys(data).length > 0 ? (
                <JsonView value={data} />
              ) : (
                <Typography variant="body1">No data</Typography>
              )}
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}
